import React from "react";
import { ChevronRight, Zap } from "lucide-react";
import { useSelector } from "react-redux";
import { selectAppUser } from "../../../redux/appAuthSlice";
import FavoriteTeamStar from "../FavoriteTeamStar";
import {
  fixtureHasFavoriteTeam,
  getBudgetFillPercent,
  getCreatorBudget,
  getJoinedCount,
  getOwner,
  getOfferedOdds,
  getP2PWinPayout,
  getRemainingCounterCapacity,
  isHotStance,
  getMinCounterStake,
  parseOutcomeParts,
} from "../../../utils/p2pHelpers";

const initials = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

/**
 * Slim exchange-ladder line for a single P2P offer inside an expanded fixture.
 * Stance + pick on top; odds / capacity / budget on wrapping lines below.
 */
const BoardOfferRow = ({ bet, selected, onSelect }) => {
  const user = useSelector(selectAppUser);
  const fixture = bet.fixture || {};
  const showsFavoriteStar = fixtureHasFavoriteTeam(fixture, user?.favoriteTeam);
  const parts = parseOutcomeParts(bet.agreedOdds, fixture);
  const owner = getOwner(bet);
  const joined = getJoinedCount(bet);
  const remainingCapacity = getRemainingCounterCapacity(bet);
  const minStake = getMinCounterStake(bet);
  const open = remainingCapacity >= minStake && bet.status === "Active";
  const hot = isHotStance(bet);
  const winPot = getP2PWinPayout(bet);
  const fillPct = getBudgetFillPercent(bet);
  const pickLabel = parts?.pickLabel || parts?.fullText || "—";
  const isAgainst = bet.stance === "against";

  return (
    <button
      type="button"
      onClick={() => onSelect?.(bet._id)}
      className={`pmb-offer${selected ? " pmb-offer-selected" : ""}${
        open ? " pmb-offer-open" : ""
      }`}
      aria-pressed={selected}
    >
      <span className="pmb-offer-fill" style={{ "--fill": `${fillPct}%` }} aria-hidden />

      <span className="pmb-offer-body">
        <span className="pmb-offer-top">
          <span className="pmb-offer-pick">
            <span
              className={`pmb-offer-stance ${isAgainst ? "is-against" : "is-for"}`}
            >
              {isAgainst ? "Against" : "For"}
            </span>
            {showsFavoriteStar ? (
              <FavoriteTeamStar size={11} className="pmb-offer-fav-star" />
            ) : null}
            <span className="pmb-offer-call" title={parts?.fullText}>
              {pickLabel}
            </span>
          </span>

          <span className="pmb-offer-top-end">
            <span className="pmb-offer-avatar" aria-hidden>
              {initials(owner.name)}
            </span>
            <ChevronRight size={13} className="pmb-offer-chev" aria-hidden />
          </span>
        </span>

        <span className="pmb-offer-terms">
          <strong>@{getOfferedOdds(bet).toFixed(2)}</strong>
          <span>
            min {minStake.toLocaleString()}
            {" · "}
            {open ? `${remainingCapacity.toLocaleString()} open` : "full"}
          </span>
        </span>

        <span className={`pmb-offer-stats${open ? " pmb-offer-stats-open" : ""}`}>
          {hot ? (
            <span className="pmb-offer-hot">
              <Zap size={8} aria-hidden /> almost full
            </span>
          ) : null}
          <span>
            {joined} counter{joined === 1 ? "" : "s"} · {fillPct}% · budget{" "}
            {getCreatorBudget(bet).toLocaleString()}
          </span>
          {open && winPot > 0 ? (
            <span className="pmb-offer-win">win {Number(winPot).toLocaleString()}</span>
          ) : null}
        </span>
      </span>
    </button>
  );
};

export default React.memo(BoardOfferRow);
